import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import ButtonToolbar from 'react-bootstrap/Dropdown'
import {
  useActiveAlertsQuery,
  useRefreshActiveAlerts,
} from '@data/alerts/alerts'

import {
  Link,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { useVisibleDevices } from '@data/device/device'

function AlertQueue() {
  const [alertIndex, setAlertIndex] = useState(1)
  const [isAlertNotFound, setIsAlertNotFound] = useState(false)
  const {
    data: queue = [],
    isError,
    isLoading,
  } = useActiveAlertsQuery()
  const devices = useVisibleDevices()
  const {
    isRefreshing,
    refresh,
  } = useRefreshActiveAlerts()
  const params = useParams()
  const navigate = useNavigate()

  // Filter on dUuid rather than deviceId: a device's deviceId can change, its UUID cannot.
  const dUuidSet = useMemo(
    () => new Set(devices.map((d) => d.dUuid)),
    [devices],
  )

  const filteredQueue = useMemo(
    () => queue.filter((q) => dUuidSet.has(q.dUuid)),
    [queue, dUuidSet],
  )

  // Gids seen in the queue on a previous pass. An id that is missing *now* but was present
  // a moment ago was just closed — the acknowledgement removes it from the active cache and
  // the redirect to the next group arrives a render later. Treating that as "Alert not
  // found" made the screen flash on every close. An id that was never in the queue is a
  // genuinely stale link and does show not-found.
  const seenGids = useRef<Set<string>>(new Set())

  useEffect(() => {
    const { id } = params
    if (isLoading) return

    const previouslySeen = seenGids.current
    seenGids.current = new Set(filteredQueue.map((group) => group.gid))

    if (filteredQueue.length === 0) {
      setIsAlertNotFound(false)
      setAlertIndex(1)
      return
    }

    if (!id) {
      navigate(`${filteredQueue[0].gid}`)
      setAlertIndex(1)
      return
    }

    const currentIndex = filteredQueue.findIndex((queue) => queue.gid === id)
    if (currentIndex === -1) {
      setIsAlertNotFound(!previouslySeen.has(id))
    } else {
      setIsAlertNotFound(false)
      setAlertIndex(currentIndex + 1)
    }
  }, [filteredQueue, isLoading, navigate, params])

  function updateAlertIndex(newIndex: number) {
    if (newIndex >= 1 && newIndex <= filteredQueue.length) {
      navigate(`${filteredQueue[newIndex - 1].gid}`)
    }
  }

  return (
    <div className="alert-queue-container">
      {isLoading ? (
        <div className="spinner-border m-auto mb-5 mt-5 d-flex text-primary" />
      ) : isError ? (
        <div className="d-flex flex-column text-center mt-5 pt-5">
          <h2>Unable to load active alerts</h2>
          <Link to={'/home'}> Go back to home page</Link>
        </div>
      ) : !isAlertNotFound && !isLoading ? (
        <>
          <h2 className="mt-3">Alert Queue</h2>
          <div className="row p-1">
            {filteredQueue.length === 0 ? (
              <div className="d-flex flex-column text-center mt-5 pt-5">
                <h2>No Alerts Active</h2>

                <Link to={'/home'}> Go back to home page</Link>
              </div>
            ) : (
              <div className="col-sm-6 ">
                <h5>
                  # {alertIndex} of {filteredQueue.length}
                </h5>
              </div>
            )}
            <div className="col-sm-6 text-right">
              {filteredQueue.length !== 0 ? (
                <ButtonToolbar aria-label="Toolbar with button groups" className="mb-3">
                  <ButtonGroup className="mr-2" aria-label="Refresh">
                    <Button
                      variant="outline-secondary"
                      disabled={isRefreshing}
                      onClick={() => refresh()}
                    >
                      {isRefreshing ? 'Refreshing…' : 'Refresh Queue'}
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup className="mr-2" aria-label="First ">
                    <Button
                      variant="outline-secondary"
                      disabled={alertIndex === 1}
                      onClick={() => updateAlertIndex(alertIndex - 1)}
                    >
                      Previous Alert
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup aria-label="Second">
                    <Button
                      variant="outline-secondary"
                      disabled={alertIndex >= filteredQueue.length}
                      onClick={() => updateAlertIndex(alertIndex + 1)}
                    >
                      Next Alert
                    </Button>
                  </ButtonGroup>

                </ButtonToolbar>
              ) : null}
            </div>

            <Outlet />
          </div>
        </>
      ) : (
        <div className="d-flex flex-column text-center mt-5 pt-5">
          <h2>Alert not found</h2>

          <Link to={'/home'}> Go back to home page</Link>
        </div>
      )}
    </div>
  )
}

export default AlertQueue
